import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { EnhancedCard } from '../components/layout/EnhancedCard';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Collapse } from '@mui/material';
import type { RootState } from '../store/store';
import { fetchOwners, createOwner, updateOwner, deleteOwner } from '../services/ownerService';
import type { Owner, CreateOwnerRequest, UpdateOwnerRequest, OwnersFilterParams } from '../services/ownerService';
import OwnerFeatureManagementPanel from '../components/owner/OwnerFeatureManagementPanel';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';

// Owner form component used for both create and edit modals
interface OwnerFormProps {
  open: boolean;
  owner: Owner | null;
  onClose: () => void;
  onSubmit: (data: CreateOwnerRequest | UpdateOwnerRequest) => void;
  isLoading: boolean;
  error: string | null;
}

const OwnerForm: React.FC<OwnerFormProps> = ({ open, owner, onClose, onSubmit, isLoading, error }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [passwordMismatchError, setPasswordMismatchError] = useState('');

  // Reset form when opening/closing or changing owner
  useEffect(() => {
    if (open) {
      setUsername(owner?.username || '');
      setPassword('');
      setConfirmPassword('');
      setIsAdmin(owner?.isAdmin || false);
      setPasswordMismatchError('');
    }
  }, [open, owner]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordMismatchError('');

    // Validate passwords match if creating new owner or changing password
    if ((!owner || password) && password !== confirmPassword) {
      setPasswordMismatchError('Passwords do not match');
      return;
    }

    // Prepare data based on whether we're creating or editing
    const formData: CreateOwnerRequest | UpdateOwnerRequest = {
      username,
      isAdmin,
    };

    // Only include password if provided (required for new owners)
    if (password) {
      formData.password = password;
    }

    onSubmit(formData);
  };

  const isCreateMode = !owner;
  const title = isCreateMode ? 'Create Owner' : 'Edit Owner';
  const buttonText = isCreateMode ? 'Create' : 'Update';

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box component='form' onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin='normal'
            required
            fullWidth
            id='username'
            label='Username'
            name='username'
            autoComplete='username'
            autoFocus
            value={username}
            onChange={e => setUsername(e.target.value)}
            disabled={isLoading}
          />
          <TextField
            margin='normal'
            required={isCreateMode}
            fullWidth
            name='password'
            label='Password'
            type='password'
            id='password'
            autoComplete='new-password'
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={isLoading}
            helperText={!isCreateMode ? 'Leave blank to keep current password' : ''}
          />
          <TextField
            margin='normal'
            required={isCreateMode || !!password}
            fullWidth
            name='confirmPassword'
            label='Confirm Password'
            type='password'
            id='confirmPassword'
            autoComplete='new-password'
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            error={!!passwordMismatchError}
            helperText={passwordMismatchError}
            disabled={isLoading}
          />
          <FormControlLabel
            control={
              <Switch checked={isAdmin} onChange={e => setIsAdmin(e.target.checked)} name='isAdmin' color='primary' disabled={isLoading} />
            }
            label='Administrator'
            sx={{ mt: 1 }}
          />
          {error && (
            <Typography color='error' variant='body2' sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={isLoading || (!isCreateMode && !username && !password)}>
          {isLoading ? <CircularProgress size={24} /> : buttonText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Delete confirmation dialog component
interface DeleteConfirmationProps {
  open: boolean;
  owner: Owner | null;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}

const DeleteConfirmation: React.FC<DeleteConfirmationProps> = ({ open, owner, onClose, onConfirm, isLoading }) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Delete Owner</DialogTitle>
      <DialogContent>
        <DialogContentText>Are you sure you want to delete owner "{owner?.username}"? This action cannot be undone.</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={onConfirm} color='error' disabled={isLoading}>
          {isLoading ? <CircularProgress size={24} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main OwnersPage component
const OwnersPage: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // State for owners data and pagination
  const [owners, setOwners] = useState<Owner[]>([]);
  const [totalOwners, setTotalOwners] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // State for filter inputs
  const [usernameFilter, setUsernameFilter] = useState('');
  const [adminFilter, setAdminFilter] = useState<boolean | undefined>(undefined);

  // State for modals
  const [formOpen, setFormOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  const handleRowExpand = (ownerId: string) => {
    setExpandedRow(expandedRow === ownerId ? null : ownerId);
  };

  // Redirect if user is not an admin
  useEffect(() => {
    if (currentUser && !currentUser.roles.includes('admin')) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  // Function to fetch owners with current pagination and filters
  const loadOwners = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: OwnersFilterParams = {
        page: page + 1, // API uses 1-based indexing
        limit: rowsPerPage,
      };

      // Add filters if set
      if (usernameFilter) {
        params.username = usernameFilter;
      }

      if (adminFilter !== undefined) {
        params.isAdmin = adminFilter;
      }

      const response = await fetchOwners(params);
      setOwners(response.items);
      setTotalOwners(response.total);
    } catch (err) {
      console.error('Failed to fetch owners:', err);
      setError('Failed to load owners. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [page, rowsPerPage, usernameFilter, adminFilter]);

  // Load owners on mount and when dependencies change
  useEffect(() => {
    loadOwners();
  }, [loadOwners]);

  // Handle pagination changes
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter changes
  const handleApplyFilters = () => {
    setPage(0); // Reset to first page when applying filters
    loadOwners();
  };

  const handleClearFilters = () => {
    setUsernameFilter('');
    setAdminFilter(undefined);
    setPage(0);
  };

  // Modal handlers
  const handleOpenCreateForm = () => {
    setSelectedOwner(null);
    setFormError(null);
    setFormOpen(true);
  };

  const handleOpenEditForm = (owner: Owner) => {
    setSelectedOwner(owner);
    setFormError(null);
    setFormOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
  };

  const handleOpenDeleteDialog = (owner: Owner) => {
    setSelectedOwner(owner);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  // CRUD operations
  const handleCreateOwner = async (data: CreateOwnerRequest) => {
    setFormError(null);
    setIsLoading(true);

    try {
      await createOwner(data);
      handleCloseForm();
      loadOwners(); // Refresh the list
    } catch (err: any) {
      console.error('Failed to create owner:', err);
      setFormError(err.response?.data?.message || 'Failed to create owner. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateOwner = async (data: UpdateOwnerRequest) => {
    if (!selectedOwner) return;

    setFormError(null);
    setIsLoading(true);

    try {
      await updateOwner(selectedOwner.id, data);
      handleCloseForm();
      loadOwners(); // Refresh the list
    } catch (err: any) {
      console.error('Failed to update owner:', err);
      setFormError(err.response?.data?.message || 'Failed to update owner. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteOwner = async () => {
    if (!selectedOwner) return;

    setIsLoading(true);

    try {
      await deleteOwner(selectedOwner.id);
      handleCloseDeleteDialog();
      loadOwners(); // Refresh the list
    } catch (err: any) {
      console.error('Failed to delete owner:', err);
      setError(err.response?.data?.message || 'Failed to delete owner. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormSubmit = (data: CreateOwnerRequest | UpdateOwnerRequest) => {
    if (selectedOwner) {
      handleUpdateOwner(data);
    } else {
      handleCreateOwner(data as CreateOwnerRequest);
    }
  };

  return (
    <PageLayout>
      <PageHeader icon={<SupervisedUserCircleIcon />} title='Owners Management' subtitle='Create, view, edit, and delete owner accounts' />

      <ContentSection title='Filters & Search'>
        <EnhancedCard variant='outlined' sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 300px' }}>
              <TextField
                fullWidth
                label='Username'
                variant='outlined'
                size='small'
                value={usernameFilter}
                onChange={e => setUsernameFilter(e.target.value)}
                placeholder='Filter by username'
              />
            </Box>
            <Box sx={{ flex: '1 1 250px' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={adminFilter === true}
                    onChange={e => {
                      if (e.target.checked) {
                        setAdminFilter(true);
                      } else if (adminFilter === true) {
                        setAdminFilter(undefined);
                      } else {
                        setAdminFilter(false);
                      }
                    }}
                  />
                }
                label={adminFilter === undefined ? 'Admin status: Any' : adminFilter ? 'Admin status: Yes' : 'Admin status: No'}
              />
            </Box>
            <Box sx={{ flex: '0 0 120px' }}>
              <Button variant='outlined' startIcon={<SearchIcon />} onClick={handleApplyFilters} fullWidth>
                Search
              </Button>
            </Box>
            <Box sx={{ flex: '0 0 120px' }}>
              <Button variant='outlined' startIcon={<ClearIcon />} onClick={handleClearFilters} fullWidth>
                Clear
              </Button>
            </Box>
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant='contained' color='primary' startIcon={<AddIcon />} onClick={handleOpenCreateForm}>
                Add
              </Button>
            </Box>
          </Box>
        </EnhancedCard>
      </ContentSection>

      <ContentSection title='Owners List'>
        <EnhancedCard>
          <CardContent>
            {error && (
              <Typography color='error' sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>Admin Status</TableCell>
                    <TableCell align='right'>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && owners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center'>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : owners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align='center'>
                        No owners found
                      </TableCell>
                    </TableRow>
                  ) : (
                    owners.map(owner => (
                      <React.Fragment key={owner.id}>
                        <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
                          <TableCell>
                            <IconButton aria-label='expand row' size='small' onClick={() => handleRowExpand(owner.id)}>
                              <ExpandMoreIcon
                                sx={{
                                  transform: expandedRow === owner.id ? 'rotate(180deg)' : 'rotate(0deg)',
                                  transition: 'transform 0.2s',
                                }}
                              />
                            </IconButton>
                          </TableCell>
                          <TableCell>{owner.id}</TableCell>
                          <TableCell>{owner.username}</TableCell>
                          <TableCell>
                            {owner.isAdmin ? (
                              <Typography color='primary' fontWeight='bold'>
                                Administrator
                              </Typography>
                            ) : (
                              'User'
                            )}
                          </TableCell>
                          <TableCell align='right'>
                            <Tooltip title='Edit'>
                              <IconButton onClick={() => handleOpenEditForm(owner)} size='small'>
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title='Delete'>
                              <IconButton onClick={() => handleOpenDeleteDialog(owner)} size='small' color='error'>
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
                            <Collapse in={expandedRow === owner.id} timeout='auto' unmountOnExit>
                              <Box sx={{ margin: 1 }}>
                                <OwnerFeatureManagementPanel owner={owner} />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component='div'
              count={totalOwners}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </EnhancedCard>
      </ContentSection>

      {/* Modals */}
      <OwnerForm
        open={formOpen}
        owner={selectedOwner}
        onClose={handleCloseForm}
        onSubmit={handleFormSubmit}
        isLoading={isLoading}
        error={formError}
      />

      <DeleteConfirmation
        open={deleteDialogOpen}
        owner={selectedOwner}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleDeleteOwner}
        isLoading={isLoading}
      />
    </PageLayout>
  );
};

export default OwnersPage;
